import React from "react";
import { SEVERITY_LABELS } from "../data/scenarios";
import { CloudRain, MapPin, Users, ArrowRight } from "@phosphor-icons/react";

const SEV_TONE = {
  low: "bg-stone-100 text-stone-700",
  elevated: "bg-amber-50 text-amber-800",
  high: "bg-orange-50 text-orange-800",
  critical: "bg-rose-50 text-rose-800",
};

export const ScenarioPanel = ({ scenario, severity }) => {
  const cond = scenario.conditions[severity];
  const sev = SEVERITY_LABELS[severity];

  return (
    <section
      data-testid="scenario-panel"
      className="panel-in w-[320px] shrink-0 h-full flex flex-col bg-stone-50/60 border border-stone-200 rounded-sm"
    >
      <PanelHeader layer="rwi" title="Scenario" subtitle="Synthetic scenario engine" />

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 scroll-soft">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500">Event</div>
          <h3 className="text-base font-medium text-stone-900 mt-0.5 leading-snug">
            {scenario.name}
          </h3>
          <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-1">
            <MapPin size={12} weight="regular" />
            {scenario.locale}
          </div>
        </div>

        <div className={`px-2.5 py-2 rounded-sm ${SEV_TONE[sev.tone]} flex items-center justify-between`}>
          <span className="text-[11px] uppercase tracking-wider font-medium">Current severity</span>
          <span className="font-mono text-xs">{severity}/4 · {sev.label}</span>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Conditions</div>
          <p className="text-sm text-stone-800 leading-relaxed">{cond.headline}</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {Object.entries(cond.metrics).map(([k, v]) => (
              <div key={k} className="bg-white border border-stone-200 rounded-sm p-2">
                <div className="font-mono text-[10px] uppercase text-stone-500 tracking-wide">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="font-mono text-sm text-stone-900 mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Affected</div>
          <div className="space-y-2">
            <Row icon={<MapPin size={12} />} label="Zones" items={scenario.affectedZones} />
            <Row icon={<Users size={12} />} label="Groups" items={scenario.affectedGroups} />
            <Row icon={<CloudRain size={12} />} label="Services" items={scenario.services} />
          </div>
        </div>

        <div className="rounded-sm border border-stone-200 bg-white p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone-500">
            <ArrowRight size={12} /> Projected next step
          </div>
          <p className="text-sm text-stone-800 mt-1.5 leading-relaxed">{cond.nextStep}</p>
        </div>

        <div className="text-[10px] text-stone-400 mt-auto pt-2 border-t border-stone-100">
          Synthetic data · scenario engine drives routing workload and KATLAS triggers.
        </div>
      </div>
    </section>
  );
};

const Row = ({ icon, label, items }) => (
  <div className="flex items-start gap-2">
    <div className="text-stone-400 mt-0.5">{icon}</div>
    <div className="flex-1">
      <div className="text-[11px] text-stone-500 font-medium">{label}</div>
      <div className="text-xs text-stone-700 leading-relaxed">{items.join(" · ")}</div>
    </div>
  </div>
);

export const PanelHeader = ({ title, subtitle, layer }) => {
  const tone = {
    rwi: "border-l-stone-400",
    bloom: "border-l-blue-500",
    katlas: "border-l-emerald-600",
    outcome: "border-l-stone-900",
    request: "border-l-amber-500",
  }[layer] || "border-l-stone-400";

  return (
    <div className={`px-4 py-3 border-b border-stone-200 bg-white border-l-2 ${tone} rounded-tl-sm`}>
      <div className="text-[11px] uppercase tracking-[0.16em] text-stone-500 font-medium">
        {subtitle}
      </div>
      <div className="text-base font-medium text-stone-900 mt-0.5">{title}</div>
    </div>
  );
};
